/*
  Agrupacion del sidebar en secciones. El menu llega ya filtrado por permisos
  (routes/menu.ts); aqui solo se ordena por seccion usando la clave "text" de
  cada item. Los items que no aparecen en ninguna seccion (Inicio, Resumen...)
  se muestran arriba, sin titulo.
*/

/** Grupo de opciones del sidebar con titulo colapsable. */
export interface SidebarSection {
  /** Clave i18n del titulo; null para el grupo superior sin titulo. */
  key: string | null;
  items: any[];
}

/** Orden de secciones y de sus opciones (claves "text" de los items del menu). */
export const SIDEBAR_SECTIONS: { key: string; items: string[] }[] = [
  {
    key: "sidebar.sections.entities",
    items: ["sidebar.admins", "sidebar.organization", "sidebar.clientes"]
  },
  {
    key: "sidebar.sections.farm",
    items: ["sidebar.farm", "sidebar.cycles-plantings", "sidebar.diets"]
  },
  {
    key: "sidebar.sections.devices",
    items: ["sidebar.alimentadores", "sidebar.waterwise", "sidebar.hydros", "sidebar.feeders"]
  },
  {
    key: "sidebar.sections.monitoring",
    items: [
      "sidebar.alertas",
      "sidebar.reports",
      "supervision",
      "sidebar.alerts",
      "sidebar.data",
      "sidebar.ponds-comparison",
      "sidebar.movimientos"
    ]
  },
  {
    key: "sidebar.sections.globals",
    items: [
      "sidebar.charolas",
      "sidebar.alimentos",
      "sidebar.diets-list",
      "sidebar.region",
      "sidebar.apks",
      "sidebar.notifications",
      "sidebar.restaurar"
    ]
  },
  {
    key: "sidebar.sections.management",
    items: ["sidebar.human-resources", "Permisos", "sidebar.operations"]
  },
  {
    key: "sidebar.sections.others",
    items: ["footer.help", "footer.product", "NAS"]
  }
];

/**
 * Items cuyo submenu se muestra como opciones de primer nivel dentro de su seccion:
 * Monitoreo del admin y Dispositivos del cliente (Alimentadores y Waterwises).
 */
export const FLATTEN_ITEMS = ["sidebar.monitoreo", "sidebar.devices"];

/** Seccion "Otros": antes eran los enlaces del footer; igual para todos los usuarios. */
export const OTHER_MENU = [
  {
    text: "footer.help",
    icon: "fa fa-question-circle",
    submenu: [
      { text: "blog.title", link: "/blog", icon: "fa fa-book", available: true },
      { text: "footer.academy", link: "/educacion", icon: "fa fa-graduation-cap", available: true },
      { text: "footer.faq", link: "/faqs", icon: "fa fa-question", available: true }
    ]
  },
  {
    text: "footer.product",
    icon: "fa fa-cube",
    submenu: [
      { text: "footer.suggestions", link: "/inbox", icon: "fa fa-comments", available: true },
      { text: "footer.version", link: "/versioning", icon: "fa fa-history", available: true }
    ]
  },
  {
    text: "NAS",
    icon: "fa fa-info-circle",
    submenu: [{ text: "footer.disclaimers", link: "/privacy", icon: "fa fa-file", available: true }]
  }
];

/**
 * Agrupa los items del menu en secciones, en el orden de SIDEBAR_SECTIONS.
 * Las secciones sin opciones (por permisos) no se devuelven.
 * @param items items del menu ya filtrados por permisos.
 * @returns grupo superior sin titulo (si tiene items) seguido de las secciones.
 */
export function buildSidebarSections(items: any[]): SidebarSection[] {
  const flat = (items || []).reduce(
    (acc: any[], item: any) =>
      item && FLATTEN_ITEMS.includes(item.text) && item.submenu
        ? [...acc, ...item.submenu]
        : [...acc, item],
    []
  );
  const sectionOf = (item: any) =>
    SIDEBAR_SECTIONS.find((section) => item && section.items.includes(item.text));

  const top: SidebarSection = { key: null, items: flat.filter((item) => !sectionOf(item)) };
  const sections: SidebarSection[] = SIDEBAR_SECTIONS.map((section) => ({
    key: section.key,
    items: section.items
      .map((text) => flat.find((item) => item && item.text === text))
      .filter((item) => !!item)
  }));

  return [top, ...sections].filter((section) => section.items.length > 0);
}
